import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Save, Edit2, X, Users, Crown, Loader2, UserPlus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import api from '../../services/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function TeamManagementSettings({ user }) {
  const [teams, setTeams] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);   // team id being edited
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(defaultForm());

  function defaultForm() {
    return { team_name: '', manager_user_id: '', manager_title: '', member_user_ids: [] };
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [teamsRes, usersRes] = await Promise.all([
        api.get(`${API}/teams`),
        api.get(`${API}/users`),
      ]);
      setTeams(teamsRes.data.teams || []);
      setAllUsers((usersRes.data.users || usersRes.data || []).filter(u => u.approved !== false));
    } catch {
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(defaultForm()); setEditing(null); setShowForm(true); };
  const openEdit = (team) => {
    setForm({
      team_name: team.team_name || '',
      manager_user_id: team.manager_user_id || '',
      manager_title: team.manager_title || '',
      member_user_ids: team.member_user_ids || [],
    });
    setEditing(team.id);
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditing(null); setForm(defaultForm()); };

  const toggleMember = (uid) => {
    setForm(f => ({
      ...f,
      member_user_ids: f.member_user_ids.includes(uid)
        ? f.member_user_ids.filter(id => id !== uid)
        : [...f.member_user_ids, uid],
    }));
  };

  const save = async () => {
    if (!form.manager_user_id) return toast.error('Select a manager');
    if (!form.manager_title.trim()) return toast.error('Enter a manager title');
    setSaving(true);
    try {
      if (editing) {
        await api.put(`${API}/teams/${editing}`, form);
        toast.success('Team updated');
      } else {
        await api.post(`${API}/teams`, form);
        toast.success('Team created');
      }
      closeForm();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteTeam = async (teamId, name) => {
    if (!window.confirm(`Delete "${name || 'this team'}"?`)) return;
    try {
      await api.delete(`${API}/teams/${teamId}`);
      toast.success('Team deleted');
      await load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const getUserName = (uid) => {
    const u = allUsers.find(x => x.id === uid);
    return u ? (u.display_name || u.username || u.email) : uid;
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Team Configuration</h3>
          <p className="text-sm text-gray-500 mt-0.5">Create and manage teams. Each team has a manager with a title and a group of members.</p>
        </div>
        <Button onClick={openCreate} className="text-white" style={{ backgroundColor: '#f97316' }}>
          <Plus className="w-4 h-4 mr-1.5" /> New Team
        </Button>
      </div>

      {/* Create / Edit Form */}
      {showForm && (
        <div className="bg-orange-50 border-2 border-orange-200 rounded-2xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-bold text-gray-800">{editing ? 'Edit Team' : 'Create New Team'}</h4>
            <button onClick={closeForm} className="p-1 rounded-lg hover:bg-orange-100 transition-colors">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold">Team Name <span className="text-gray-400 font-normal">(optional)</span></Label>
              <Input
                value={form.team_name}
                onChange={e => setForm(f => ({ ...f, team_name: e.target.value }))}
                placeholder="e.g. Sales North"
                className="mt-1"
                data-testid="team-form-name"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Manager Title <span className="text-red-500">*</span></Label>
              <Input
                value={form.manager_title}
                onChange={e => setForm(f => ({ ...f, manager_title: e.target.value }))}
                placeholder="e.g. Head of Sales"
                className="mt-1"
                data-testid="team-form-title"
              />
            </div>
          </div>

          {/* Manager picker */}
          <div>
            <Label className="text-xs font-semibold flex items-center gap-1">
              <Crown className="w-3 h-3" style={{ color: '#f97316' }} />
              Manager <span className="text-red-500">*</span>
            </Label>
            <select
              value={form.manager_user_id}
              onChange={e => setForm(f => ({ ...f, manager_user_id: e.target.value }))}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-200"
              data-testid="team-form-manager"
            >
              <option value="">— Select manager —</option>
              {allUsers.map(u => (
                <option key={u.id} value={u.id}>
                  {u.display_name || u.username || u.email}
                </option>
              ))}
            </select>
          </div>

          {/* Member picker */}
          <div>
            <Label className="text-xs font-semibold flex items-center gap-1">
              <Users className="w-3 h-3 text-gray-500" />
              Team Members ({form.member_user_ids.length} selected)
            </Label>
            <div className="mt-1 max-h-52 overflow-y-auto border border-gray-200 rounded-xl bg-white divide-y divide-gray-50">
              {allUsers.map(u => {
                const uid = u.id;
                const checked = form.member_user_ids.includes(uid);
                const isManager = uid === form.manager_user_id;
                return (
                  <label
                    key={uid}
                    className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                      checked ? 'bg-orange-50' : 'hover:bg-gray-50'
                    } ${isManager ? 'opacity-50 pointer-events-none' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={isManager}
                      onChange={() => toggleMember(uid)}
                      className="accent-orange-500 rounded"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-800 truncate">
                        {u.display_name || u.username}
                        {isManager && <span className="text-[10px] text-orange-500 ml-1">(manager)</span>}
                      </div>
                      <div className="text-[11px] text-gray-400 truncate">{u.email}</div>
                    </div>
                  </label>
                );
              })}
              {allUsers.length === 0 && (
                <div className="px-3 py-4 text-center text-sm text-gray-400">No users found</div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={closeForm} size="sm">Cancel</Button>
            <Button onClick={save} disabled={saving} size="sm" className="text-white" style={{ backgroundColor: '#f97316' }}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
              {editing ? 'Update Team' : 'Create Team'}
            </Button>
          </div>
        </div>
      )}

      {/* Teams list */}
      {loading && (
        <div className="text-center py-10 text-gray-400 text-sm">Loading teams…</div>
      )}

      {!loading && teams.length === 0 && !showForm && (
        <div className="text-center py-14 border-2 border-dashed border-gray-200 rounded-2xl">
          <UserPlus className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No teams yet</p>
          <p className="text-sm text-gray-400 mt-1">Click "New Team" to create your first team.</p>
        </div>
      )}

      {!loading && teams.length > 0 && (
        <div className="space-y-3">
          {teams.map(team => (
            <div key={team.id} className="bg-white border border-gray-200 rounded-2xl p-4 flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {team.team_name && (
                    <span className="font-bold text-gray-900">{team.team_name}</span>
                  )}
                  <span
                    className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: '#fff7ed', color: '#f97316' }}
                  >
                    {team.manager_title}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 mt-1">
                  <Crown className="w-3 h-3 flex-shrink-0" style={{ color: '#f97316' }} />
                  <span className="text-sm font-medium text-gray-700">{team.manager_name || getUserName(team.manager_user_id)}</span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(team.member_names || []).map((name, i) => (
                    <span key={i} className="text-xs bg-gray-100 text-gray-700 rounded-full px-2 py-0.5 font-medium">{name}</span>
                  ))}
                  {(!team.member_names || team.member_names.length === 0) && (
                    <span className="text-xs text-gray-400 italic">No members</span>
                  )}
                </div>
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                <button
                  onClick={() => openEdit(team)}
                  className="p-2 rounded-lg hover:bg-orange-50 transition-colors"
                  title="Edit team"
                  data-testid={`team-edit-${team.id}`}
                >
                  <Edit2 className="w-4 h-4" style={{ color: '#f97316' }} />
                </button>
                <button
                  onClick={() => deleteTeam(team.id, team.team_name)}
                  className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                  title="Delete team"
                  data-testid={`team-delete-${team.id}`}
                >
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
